import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function ServicesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('company', formData.company);
      body.append('phone', formData.phone);
      body.append('message', formData.message);
      const res = await fetch('https://readdy.ai/api/form/d5no2bcl56fkiit4nr80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 4000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 4000);
    }
  };

  const serviceStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "AI Marketing & Automation Services",
    "provider": {
      "@type": "Organization",
      "name": "Technelle.ai"
    },
    "areaServed": {
      "@type": "State",
      "name": "Wisconsin"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Marketing & AI Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Brand Positioning & Messaging"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Opportunity Assessment"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Workflow Automation"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Demand Generation & Lead Funnels"
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="AI Marketing & Consulting Services | Technelle"
        description="Comprehensive AI marketing and consulting services for Wisconsin businesses. From strategy development to implementation, we help Waukesha and Wisconsin companies leverage AI for measurable growth. Services include demand generation, workflow automation, AI implementation, and strategic marketing."
        keywords="AI marketing services Wisconsin, marketing consulting Waukesha, AI implementation Wisconsin, demand generation services, workflow automation, marketing strategy Wisconsin, AI consulting services, Wisconsin business consulting"
        canonical="https://technelle.ai/services"
        ogTitle="AI Marketing & Consulting Services | Technelle"
        ogDescription="Transform your Wisconsin business with AI-powered marketing services. Strategy, implementation, and optimization designed for measurable growth."
        ogUrl="https://technelle.ai/services"
        structuredData={serviceStructuredData}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle Logo" className="h-14" loading="eager" fetchPriority="high" decoding="async" width="56" height="56" />
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/#about" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[16px] text-[#14B8A6] font-medium transition-colors">Services</Link>
            <Link to="/#case-studies" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Case Studies</Link>
            <Link to="/resources" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Resources</Link>
            <Link to="/how-we-work" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">How We Work</Link>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPanelOpen(true)}
              className="hidden lg:block bg-[#14B8A6] text-white px-6 py-2.5 rounded-full text-[16px] font-semibold hover:bg-[#0F9688] transition-colors whitespace-nowrap shadow-lg cursor-pointer"
            >
              Book Free Strategy Call
            </button>
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-[#1a1a1a] cursor-pointer"
              aria-label="Open mobile menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-[200] lg:hidden" onClick={() => setMobileNavOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-20 border-b border-gray-100">
              <span className="text-[#1a1a1a] font-semibold text-[16px]">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 flex items-center justify-center text-[#1a1a1a] cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-6">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Home</Link>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">About</Link>
              <Link to="/services" onClick={() => setMobileNavOpen(false)} className="text-[#14B8A6] text-[16px] font-medium">Services</Link>
              <Link to="/resources" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Resources</Link>
              <Link to="/how-we-work" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">How We Work</Link>
            </nav>
            <div className="px-6 mt-auto pb-8">
              <button
                onClick={() => { setMobileNavOpen(false); setPanelOpen(true); }}
                className="w-full bg-[#14B8A6] text-white py-4 rounded-full text-[15px] font-semibold hover:bg-[#0F9688] transition-colors cursor-pointer"
              >
                Book Free Strategy Call
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Form Panel Overlay */}
      {panelOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto">
            <div className="p-8 sm:p-10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-[22px] font-bold text-[#1a1a1a]">Let's talk —</h2>
                  <p className="text-[14px] text-gray-500 mt-1">We'll get back to you within 24 hours.</p>
                </div>
                <button
                  onClick={() => setPanelOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#14B8A6] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close"
                >
                  <i className="ri-close-line text-[22px]"></i>
                </button>
              </div>

              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-5">
                    <i className="ri-check-line text-green-600 text-[28px]"></i>
                  </div>
                  <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-2">Message sent!</h3>
                  <p className="text-[15px] text-gray-600">We'll be in touch within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="services-lets-talk-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1a1a1a] mb-1.5" htmlFor="sv-name">Full Name *</label>
                      <input
                        id="sv-name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Jane Smith"
                        className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1a1a1a] mb-1.5" htmlFor="sv-email">Email Address *</label>
                      <input
                        id="sv-email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jane@company.com"
                        className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1a1a1a] mb-1.5" htmlFor="sv-company">Company</label>
                      <input
                        id="sv-company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Acme Inc."
                        className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1a1a1a] mb-1.5" htmlFor="sv-phone">Phone Number</label>
                      <input
                        id="sv-phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="414-000-0000"
                        className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#14B8A6] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1a1a1a] mb-1.5" htmlFor="sv-message">What are you looking to solve? *</label>
                    <textarea
                      id="sv-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us a bit about your business and what you'd like to automate or improve..."
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#14B8A6] transition-colors resize-none"
                    />
                    <div className="text-[12px] text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-[13px] text-red-500">Something went wrong. Please try again or email us directly at melissa@technelle.ai.</p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-[#14B8A6] text-white py-4 rounded-md text-[15px] font-semibold hover:bg-[#0F9688] transition-colors cursor-pointer whitespace-nowrap min-h-[44px] disabled:opacity-60"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send My Message'}
                  </button>

                  <p className="text-[13px] text-gray-500 text-center">
                    Or call us directly:{' '}
                    <a href="tel:4145309114" className="text-[#14B8A6] hover:underline font-medium">414-530-9114</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-4xl mx-auto mt-12">
            <h1 className="text-[36px] sm:text-[48px] md:text-[56px] lg:text-6xl font-bold text-gray-900 mb-6">
              Full-Funnel Marketing & AI Implementation Services
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              From strategy to execution, we help Wisconsin businesses leverage AI to attract, convert, and retain customers at every stage of the buyer journey.
            </p>
            <Link
              to="/services/ai-consulting"
              className="inline-flex items-center gap-3 bg-[#2196F3] text-white px-8 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
            >
              <i className="ri-robot-line text-xl" aria-hidden="true"></i>
              <span>See AI Consulting for SMBs — Save $30K–$80K/yr</span>
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Marketing Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#1a1a1a] rounded-xl flex items-center justify-center">
                <i className="ri-megaphone-line text-white text-3xl" aria-hidden="true"></i>
              </div>
              <h2 className="text-[48px] font-bold text-[#1a1a1a]">Marketing Services</h2>
            </div>
            <p className="text-[18px] text-[#666666] max-w-3xl leading-relaxed">
              <strong className="text-[#1a1a1a]">Build a marketing engine that consistently delivers qualified leads for your Wisconsin business.</strong> From brand positioning to demand generation, we create comprehensive strategies that drive sustainable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Brand Positioning & Messaging */}
            <div className="bg-[#f8f8f8] rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Brand Positioning & Messaging</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Stand out in crowded Wisconsin markets with a clear, compelling value proposition. We help you articulate what makes your business unique and why customers should choose you.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Competitive analysis and market positioning strategy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Brand voice and messaging framework development</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Value proposition that resonates with your target audience</span>
                </li>
              </ul>
            </div>

            {/* Website Design & Optimization */}
            <div className="bg-[#f8f8f8] rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-layout-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Website Design & Optimization</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Transform your website into a lead generation machine for your Wisconsin business. We design high-converting sites optimized for both user experience and search engines.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Conversion-focused web design and development</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Technical SEO optimization for higher rankings</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">A/B testing and continuous performance improvement</span>
                </li>
              </ul>
            </div>

            {/* Demand Generation */}
            <div className="bg-[#f8f8f8] rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-rocket-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Demand Generation & Lead Funnels</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Build systematic lead generation engines that deliver qualified Wisconsin prospects consistently. From awareness to conversion, we optimize every stage of your funnel.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Multi-channel campaign strategy and execution</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Lead magnet creation and conversion funnel design</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Paid advertising management across Google, LinkedIn, and Meta</span>
                </li>
              </ul>
            </div>

            {/* Content Strategy */}
            <div className="bg-[#f8f8f8] rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-article-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Content Strategy & Creation</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Create content that educates, engages, and converts Wisconsin audiences. We develop strategic content across all channels that positions you as an industry authority.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Strategic content calendar and editorial planning</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Blog articles, social media, and email campaigns</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">SEO-optimized content that ranks and converts</span>
                </li>
              </ul>
            </div>

            {/* Meta Advertising & Social Content */}
            <div className="bg-[#f8f8f8] rounded-2xl p-8 sm:p-10 hover:shadow-xl transition-all col-span-1 md:col-span-2">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1">
                  <div className="w-14 h-14 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-6">
                    <i className="ri-meta-line text-white text-2xl" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-2">Meta Ads That Actually Convert</h3>
                  <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                    Most Wisconsin small businesses are wasting their Meta ad budget on boosted posts with no strategy. We build and manage conversion-focused Meta ad campaigns — from audience targeting and creative to landing page optimization and retargeting — that turn social media spend into measurable revenue. Pair that with a consistent content strategy that builds local trust and keeps your business top of mind.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {[
                      'Meta ad campaign buildout and management',
                      'Conversion-optimized ad creative and copy',
                      'Audience targeting and retargeting strategy',
                      'Website conversion tracking and pixel setup',
                      'Organic content strategy driving website traffic',
                      'Monthly performance reporting with real metrics',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <i className="ri-check-line text-[#14B8A6] text-xl mt-1 flex-shrink-0" aria-hidden="true"></i>
                        <span className="text-[15px] text-[#1a1a1a]">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lg:w-80 flex-shrink-0 flex flex-col justify-center">
                  <div className="bg-[#1a1a1a] rounded-2xl p-8 text-white">
                    <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Real Result</div>
                    <div className="text-[52px] font-bold text-white leading-none mb-2">206%</div>
                    <div className="text-[16px] text-white/80 leading-relaxed">
                      increase in website traffic for <strong className="text-white">Tipsy Tee Tavern</strong> after complete website overhaul and Meta-driven content strategy.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics & Reporting */}
            <div className="bg-[#f8f8f8] rounded-2xl p-8 sm:p-10 hover:shadow-xl transition-all col-span-1 md:col-span-2">
              <div className="w-14 h-14 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-bar-chart-box-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Analytics, Reporting & Performance Optimization</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed max-w-3xl">
                Make data-driven decisions for your Wisconsin business with comprehensive tracking and reporting. We measure what matters and continuously optimize for better results.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Custom dashboard and KPI tracking systems</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Monthly performance reports with actionable insights</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Conversion rate optimization and testing</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA After Marketing Services */}
          <div className="text-center mt-16">
            <button
              onClick={() => setPanelOpen(true)}
              className="bg-[#14B8A6] text-white px-10 py-4 rounded-lg text-[16px] font-semibold hover:bg-[#0F9688] transition-all inline-block whitespace-nowrap shadow-lg cursor-pointer"
            >
              Discuss Your Wisconsin Marketing Needs
            </button>
          </div>
        </div>
      </section>

      {/* Wisconsin Service Areas — contextual internal links */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Where We Work</div>
            <h2 className="text-[36px] font-serif font-bold text-[#1a1a1a] mb-4">AI Marketing Services Across Wisconsin</h2>
            <p className="text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Based in Waukesha, we deliver the same senior-level AI marketing expertise to businesses in every major Wisconsin market — no travel fees, no junior account managers.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <Link to="/service-areas/milwaukee" className="bg-gray-50 rounded-xl p-7 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
              <div className="text-[20px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Milwaukee, WI</div>
              <p className="text-[14px] text-gray-600 leading-relaxed mb-4">Wisconsin's largest market. We help Milwaukee manufacturers, HVAC companies, healthcare practices, and professional services firms generate more qualified leads with AI-powered marketing.</p>
              <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Milwaukee AI marketing services <i className="ri-arrow-right-line"></i></span>
            </Link>
            <Link to="/service-areas/madison" className="bg-gray-50 rounded-xl p-7 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
              <div className="text-[20px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Madison, WI</div>
              <p className="text-[14px] text-gray-600 leading-relaxed mb-4">Wisconsin's capital and tech hub. We help Madison startups, UW-adjacent businesses, and professional firms scale faster with AI automation and demand generation.</p>
              <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Madison AI marketing services <i className="ri-arrow-right-line"></i></span>
            </Link>
            <Link to="/service-areas/green-bay" className="bg-gray-50 rounded-xl p-7 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
              <div className="text-[20px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Green Bay, WI</div>
              <p className="text-[14px] text-gray-600 leading-relaxed mb-4">Northeast Wisconsin's hub. We help Green Bay manufacturers, hospitality businesses, and B2B companies fill their pipelines and build sustainable revenue growth.</p>
              <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Green Bay AI marketing services <i className="ri-arrow-right-line"></i></span>
            </Link>
          </div>
          <div className="text-center">
            <Link to="/service-areas" className="inline-flex items-center gap-2 text-[#2196F3] text-[14px] font-semibold hover:underline">
              View all Wisconsin service areas <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Services Section */}
      <section className="py-24 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#14B8A6] rounded-xl flex items-center justify-center">
                <i className="ri-brain-line text-white text-3xl" aria-hidden="true"></i>
              </div>
              <h2 className="text-[48px] font-bold text-[#1a1a1a]">AI Consulting & Implementation</h2>
              <div className="bg-[#14B8A6] text-white text-[11px] uppercase tracking-wider px-4 py-2 rounded-full font-semibold ml-4">
                Core Differentiator
              </div>
            </div>
            <p className="text-[18px] text-[#666666] max-w-3xl leading-relaxed">
              <strong className="text-[#1a1a1a]">Practical, business-ready AI that delivers measurable ROI for Wisconsin businesses.</strong> We implement AI solutions that save time, reduce costs, and increase efficiency — no hype, just results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* AI Opportunity Assessment */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#14B8A6] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-search-eye-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">AI Opportunity Assessment</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Discover where AI can deliver the biggest impact in your Wisconsin business. We audit your operations, identify opportunities, and calculate potential ROI before you invest a dollar.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Comprehensive process audit and opportunity mapping</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">ROI analysis and implementation prioritization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Detailed implementation roadmap with timelines</span>
                </li>
              </ul>
            </div>

            {/* Custom AI Tools */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#14B8A6] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-tools-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Custom AI Tool Design & Development</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Build AI solutions tailored to your specific Wisconsin business needs. From intelligent chatbots to predictive analytics, we create tools that solve real problems and deliver measurable value.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">AI chatbots for 24/7 customer service automation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Predictive analytics and business forecasting tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">AI-powered content generation and creative tools</span>
                </li>
              </ul>
            </div>

            {/* Workflow Automation */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#14B8A6] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-loop-right-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">Workflow Automation</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Eliminate repetitive tasks and streamline operations for your Wisconsin business with intelligent automation. Free your team to focus on high-value work that actually grows your business.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">End-to-end process automation and system integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Automated data entry and document processing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Email and communication workflow automation</span>
                </li>
              </ul>
            </div>

            {/* AI-Enabled Marketing */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#14B8A6] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-lightbulb-flash-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <h3 className="text-[28px] font-bold text-[#1a1a1a] mb-4">AI-Enabled Marketing Systems</h3>
              <p className="text-[16px] text-[#666666] mb-6 leading-relaxed">
                Supercharge your Wisconsin marketing with AI-powered personalization, intelligent lead scoring, and predictive campaign optimization that drives better results with less effort.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">AI-powered lead scoring and automated nurturing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Personalized content recommendations at scale</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#14B8A6] text-xl mt-1" aria-hidden="true"></i>
                  <span className="text-[15px] text-[#1a1a1a]">Predictive campaign performance and optimization</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA After AI Services */}
          <div className="text-center mt-16">
            <button
              onClick={() => setPanelOpen(true)}
              className="bg-[#14B8A6] text-white px-10 py-4 rounded-lg text-[16px] font-semibold hover:bg-[#0F9688] transition-all inline-block whitespace-nowrap shadow-lg cursor-pointer"
            >
              Get Your Free AI Roadmap
            </button>
          </div>
        </div>
      </section>

      {/* Engagement Types */}
      <section className="py-24 bg-[#faf8f5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-6">Service Tiers</h2>
            <p className="text-[18px] text-[#666666] max-w-2xl mx-auto leading-relaxed">
              Start where it makes sense for your business. Most clients begin with the Audit and move into implementation from there.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* AI Audit & Roadmap */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-14 h-14 bg-[#2196F3]/10 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-map-2-line text-[#2196F3] text-2xl" aria-hidden="true"></i>
              </div>
              <div className="text-[12px] uppercase tracking-widest text-gray-400 font-semibold mb-2">One-Time Engagement</div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">AI Audit &amp; Roadmap</h3>
              <p className="text-[15px] text-[#666666] leading-relaxed mb-6">
                The fastest way to know exactly where AI can save your business money. Walk away with a prioritized savings roadmap — no guesswork, no wasted spend.
              </p>
              <ul className="space-y-2 mb-8">
                {['Full operational workflow audit', 'AI opportunity identification & cost analysis', 'Prioritized implementation roadmap', 'Tool recommendations with ROI estimates'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    <span className="text-[14px] text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services/ai-consulting" className="block w-full text-center bg-[#1a1a1a] text-white py-3 rounded-md text-[14px] font-semibold hover:bg-[#333] transition-colors whitespace-nowrap">
                Learn More
              </Link>
            </div>

            {/* AI Growth Accelerator */}
            <div className="bg-[#1a1a1a] rounded-2xl p-10 ring-2 ring-[#2196F3] relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-[#2196F3] text-white text-[11px] uppercase tracking-widest font-bold px-4 py-1.5 rounded-full whitespace-nowrap">Most Popular</span>
              </div>
              <div className="w-14 h-14 bg-[#2196F3] rounded-xl flex items-center justify-center mb-5">
                <i className="ri-rocket-2-line text-white text-2xl" aria-hidden="true"></i>
              </div>
              <div className="text-[12px] uppercase tracking-widest text-[#2196F3] font-semibold mb-2">90-Day Implementation Retainer</div>
              <h3 className="text-[24px] font-serif font-bold text-white mb-4">AI Growth Accelerator</h3>
              <p className="text-[15px] text-white/80 leading-relaxed mb-6">
                We don't just hand you a plan — we build it with you. Over 90 days, we implement the highest-impact AI systems and get them running.
              </p>
              <ul className="space-y-2 mb-8">
                {['Everything in AI Audit & Roadmap', 'Hands-on implementation of top 3 AI systems', 'Marketing automation setup & launch', 'AI content system build-out', 'Weekly check-ins + performance reporting'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    <span className="text-[14px] text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services/ai-consulting" className="block w-full text-center bg-[#2196F3] text-white py-3 rounded-md text-[14px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap">
                Learn More
              </Link>
            </div>

            {/* Fractional CMO */}
            <div className="bg-white rounded-2xl p-10 hover:shadow-xl transition-all border border-gray-100">
              <div className="w-14 h-14 bg-[#2196F3]/10 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-user-star-line text-[#2196F3] text-2xl" aria-hidden="true"></i>
              </div>
              <div className="text-[12px] uppercase tracking-widest text-gray-400 font-semibold mb-2">Ongoing Senior Leadership</div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Fractional CMO + AI Integration</h3>
              <p className="text-[15px] text-[#666666] leading-relaxed mb-6">
                Senior marketing leadership with full AI integration — without the $200K+ CMO salary. Strategic direction, execution oversight, and continuous AI optimization.
              </p>
              <ul className="space-y-2 mb-8">
                {['Everything in AI Growth Accelerator', 'Ongoing fractional CMO leadership', 'Full AI stack management & optimization', 'Team training and capability building', 'Monthly strategy sessions + board-ready reporting'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className="ri-check-line text-[#2196F3] text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    <span className="text-[14px] text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services/ai-consulting" className="block w-full text-center bg-[#1a1a1a] text-white py-3 rounded-md text-[14px] font-semibold hover:bg-[#333] transition-colors whitespace-nowrap">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="py-24 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-6">
            <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Ideal Client Profile</div>
            <h2 className="text-[36px] sm:text-[48px] font-serif font-bold text-white mb-6 leading-tight">
              Built for Wisconsin Businesses Doing $1M to $30M
            </h2>
            <p className="text-[17px] sm:text-[18px] text-white/75 max-w-3xl mx-auto leading-relaxed">
              Technelle is not built for startups with no budget or enterprises with a full marketing department. We work with the businesses in between — established Wisconsin companies with real revenue, real staff, and real growth goals that are being held back by outdated marketing and untapped AI potential.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-14 mb-14">
            {/* Card 1 — The trades business */}
            <div className="bg-[#0d1f35] border border-white/10 rounded-2xl p-8 hover:border-[#2196F3]/50 transition-all">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 bg-[#2196F3]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-tools-line text-[#2196F3] text-xl" aria-hidden="true"></i>
                </div>
                <div className="text-[12px] uppercase tracking-widest text-[#2196F3] font-semibold">The Trades Business</div>
              </div>
              <p className="text-[16px] text-white/85 leading-relaxed">
                An HVAC, plumbing, or electrical company doing $1M to $10M annually with 5 to 30 employees. Great at the work. Invisible online. Missing leads every week.
              </p>
            </div>

            {/* Card 2 — The local institution */}
            <div className="bg-[#0d1f35] border border-white/10 rounded-2xl p-8 hover:border-[#2196F3]/50 transition-all">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 bg-[#2196F3]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-store-2-line text-[#2196F3] text-xl" aria-hidden="true"></i>
                </div>
                <div className="text-[12px] uppercase tracking-widest text-[#2196F3] font-semibold">The Local Institution</div>
              </div>
              <p className="text-[16px] text-white/85 leading-relaxed">
                A restaurant, venue, or hospitality business doing $500K to $5M with a loyal following that has never been properly marketed. Huge untapped revenue sitting in their customer list.
              </p>
            </div>

            {/* Card 3 — The regional manufacturer */}
            <div className="bg-[#0d1f35] border border-white/10 rounded-2xl p-8 hover:border-[#2196F3]/50 transition-all">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 bg-[#2196F3]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-2-line text-[#2196F3] text-xl" aria-hidden="true"></i>
                </div>
                <div className="text-[12px] uppercase tracking-widest text-[#2196F3] font-semibold">The Regional Manufacturer</div>
              </div>
              <p className="text-[16px] text-white/85 leading-relaxed">
                A Wisconsin manufacturing or industrial company doing $5M to $30M with complex operations and staff spending significant time on tasks AI can handle in minutes.
              </p>
            </div>

            {/* Card 4 — The professional services firm */}
            <div className="bg-[#0d1f35] border border-white/10 rounded-2xl p-8 hover:border-[#2196F3]/50 transition-all">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 bg-[#2196F3]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-briefcase-4-line text-[#2196F3] text-xl" aria-hidden="true"></i>
                </div>
                <div className="text-[12px] uppercase tracking-widest text-[#2196F3] font-semibold">The Professional Services Firm</div>
              </div>
              <p className="text-[16px] text-white/85 leading-relaxed">
                A law firm, accounting practice, medical office, or consulting firm doing $1M to $15M with high-value clients and almost no systematic marketing or follow-up in place.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[17px] text-white/70 leading-relaxed mb-8">
              If your business fits one of these profiles — or something close to it — the free 30-minute audit is where we start. No pitch. Just clarity on where your biggest opportunities are.
            </p>
            <a
              href="https://calendly.com/melissa-technelle/30min?utm_source=technelle&utm_medium=website&utm_campaign=audit&utm_content=services-page"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#2196F3] text-white px-10 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer"
            >
              See If We Are a Fit
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[36px] sm:text-[48px] lg:text-[56px] font-serif font-bold text-white mb-8 leading-tight">Ready to Get Started?</h2>
          <p className="text-[20px] text-[#cccccc] mb-12 max-w-2xl mx-auto leading-relaxed">
            Book a free strategy call to discuss your Wisconsin business goals and explore how we can help you grow.
          </p>
          <button
            onClick={() => setPanelOpen(true)}
            className="bg-[#14B8A6] text-white px-12 py-5 rounded-lg text-[18px] font-medium hover:bg-[#0F9688] transition-all inline-block whitespace-nowrap cursor-pointer"
          >
            Book Your Strategy Call
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white rounded-t-[32px] py-20 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-4 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle Logo" className="h-10" loading="lazy" decoding="async" width="40" height="40" />
                <span className="text-[20px] font-bold text-white">Technelle</span>
              </div>
              <p className="text-[16px] text-[#999999] mb-10">Strategic Growth Through Marketing & AI</p>
            </div>

            <div>
              <div className="text-[14px] uppercase tracking-wider text-[#999999] mb-6">Quick Links</div>
              <nav className="space-y-4">
                <Link to="/services" className="block text-white hover:text-[#14B8A6] transition-colors">Services</Link>
                <Link to="/how-we-work" className="block text-white hover:text-[#14B8A6] transition-colors">How We Work</Link>
                <Link to="/case-studies" className="block text-white hover:text-[#14B8A6] transition-colors">Case Studies</Link>
                <Link to="/about" className="block text-white hover:text-[#14B8A6] transition-colors">About</Link>
                <Link to="/industries" className="block text-white hover:text-[#14B8A6] transition-colors">Industries</Link>
                <Link to="/resources" className="block text-white hover:text-[#14B8A6] transition-colors">Resources</Link>
                <Link to="/faq" className="block text-white hover:text-[#14B8A6] transition-colors">FAQ</Link>
                <Link to="/service-areas" className="block text-white hover:text-[#14B8A6] transition-colors">Service Areas</Link>
                <Link to="/contact" className="block text-white hover:text-[#14B8A6] transition-colors">Contact</Link>
              </nav>
            </div>

            <div>
              <div className="text-[14px] uppercase tracking-wider text-[#999999] mb-6">Get Started</div>
              <div className="text-[24px] text-white mb-2">414-530-9114</div>
              <div className="text-[16px] text-[#999999] mb-8">melissa@technelle.ai</div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-8">
                <a href="#" className="text-[#999999] hover:text-white transition-colors">
                  <i className="ri-linkedin-fill text-2xl"></i>
                </a>
                <a href="#" className="text-[#999999] hover:text-white transition-colors">
                  <i className="ri-twitter-x-fill text-2xl"></i>
                </a>
              </div>
              <div className="text-[14px] text-[#999999]">© 2025 Technelle. All rights reserved.</div>
              <div className="flex items-center gap-4 flex-wrap justify-center">
                <Link to="/privacy-policy" className="text-[14px] text-[#999999] hover:text-white transition-colors">Privacy Policy</Link>
                <span className="text-[#333333]">|</span>
                <Link to="/terms" className="text-[14px] text-[#999999] hover:text-white transition-colors">Terms</Link>
                <span className="text-[#333333]">|</span>

              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
